import cancerImg from "../images/cancer.jpg";
import segurosImg from "../images/8seguros.jpg";
import gastosImg from "../images/gastos-finales.jpg";
import retiroImg from "../images/retirement.jpg";

/**
 * Productos que la agencia coloca hoy. Cada uno incluye lo que el
 * visitante necesita para decidir: para quién es, qué resuelve,
 * qué cubre, qué NO cubre y qué preguntar antes de firmar.
 * La transparencia sobre lo que no cubre es deliberada: es el
 * diferenciador frente al resto de las agencias del mercado.
 */

export const PRODUCTS = [
  {
    id: "8-seguros-en-1",
    image: segurosImg,
    icon: "layers",
    featured: true,
    name: { es: "8 Seguros en 1", en: "8 Insurances in 1" },
    tag: { es: "El más solicitado", en: "Most requested" },
    promise: {
      es: "Ocho coberturas en una sola póliza, con un solo pago y un solo asesor.",
      en: "Eight coverages in a single policy, one payment, one advisor.",
    },
    forWho: {
      es: "Familias y profesionales que quieren cobertura amplia sin manejar cuatro pólizas distintas.",
      en: "Families and professionals who want broad coverage without juggling four separate policies.",
    },
    summary: {
      es: "Combina cáncer, accidentes, hospitalización, enfermedades temidas, trasplante, incapacidad, convalecencia y gastos funerales en un solo plan. Paga en efectivo directamente a ti, además de lo que cubra tu plan médico.",
      en: "Combines cancer, accident, hospitalization, dreaded diseases, transplant, disability, convalescence and funeral expenses in one plan. It pays cash directly to you, on top of whatever your health plan covers.",
    },
    coverages: [
      {
        amount: "$57,000",
        title: { es: "Primer diagnóstico de cáncer", en: "First cancer diagnosis" },
        detail: {
          es: "Pago en efectivo al confirmarse el diagnóstico, más de 50 beneficios asociados al tratamiento.",
          en: "Cash paid on confirmed diagnosis, plus 50+ treatment-related benefits.",
        },
      },
      {
        amount: "$100,000",
        title: { es: "Muerte accidental y desmembramiento", en: "Accidental death & dismemberment" },
        detail: {
          es: "Fracturas, dislocaciones, $75 por punto de sutura, visitas médicas y más de 40 beneficios de accidente.",
          en: "Fractures, dislocations, $75 per stitch, doctor visits and 40+ accident benefits.",
        },
      },
      {
        amount: "$300",
        title: { es: "Por día de hospitalización", en: "Per day of hospitalization" },
        detail: {
          es: "Hospitalización ilimitada mientras la póliza esté activa.",
          en: "Unlimited hospitalization while the policy is active.",
        },
      },
      {
        amount: "50",
        title: { es: "Enfermedades temidas cubiertas", en: "Dreaded diseases covered" },
        detail: {
          es: "Parkinson, leptospirosis, AH1N1, VIH, chikungunya, fibromialgia, entre otras.",
          en: "Parkinson's, leptospirosis, AH1N1, HIV, chikungunya, fibromyalgia, among others.",
        },
      },
      {
        amount: "$30,000",
        title: { es: "Trasplante de órganos", en: "Organ transplant" },
        detail: {
          es: "Respaldo económico para el proceso de trasplante y su recuperación.",
          en: "Financial backing for the transplant process and recovery.",
        },
      },
      {
        amount: "$1,000",
        title: { es: "Mensuales por incapacidad", en: "Monthly disability benefit" },
        detail: {
          es: "Ingreso de reemplazo mientras no puedas trabajar.",
          en: "Replacement income while you cannot work.",
        },
      },
      {
        amount: "$100",
        title: { es: "Por día de convalecencia en casa", en: "Per day of home convalescence" },
        detail: {
          es: "Cubre el período de recuperación fuera del hospital.",
          en: "Covers the recovery period outside the hospital.",
        },
      },
      {
        amount: "$7,500",
        title: { es: "Gastos funerales", en: "Funeral expenses" },
        detail: {
          es: "Si no se gana la lucha contra una enfermedad cubierta bajo la póliza.",
          en: "If the fight against a covered illness is not won.",
        },
      },
    ],
    notCovered: {
      es: [
        "No sustituye tu plan médico: es un complemento que paga en efectivo.",
        "Condiciones preexistentes pueden tener período de espera según la aseguradora.",
        "Los beneficios se activan con evidencia médica del diagnóstico o evento.",
      ],
      en: [
        "It does not replace your health plan: it is a complement that pays cash.",
        "Pre-existing conditions may carry a waiting period depending on the carrier.",
        "Benefits activate with medical evidence of the diagnosis or event.",
      ],
    },
    ask: {
      es: [
        "¿Cuál es el período de espera para cada beneficio?",
        "¿La prima se mantiene fija o sube con la edad?",
        "¿Qué necesito para radicar una reclamación y en cuánto tiempo paga?",
      ],
      en: [
        "What is the waiting period for each benefit?",
        "Is the premium level or does it increase with age?",
        "What do I need to file a claim and how fast does it pay?",
      ],
    },
    plans: [
      {
        id: "individual",
        name: { es: "Individual", en: "Individual" },
        detail: { es: "Una persona asegurada. 100% de los beneficios.", en: "One insured person. 100% of benefits." },
      },
      {
        id: "pareja",
        name: { es: "Pareja", en: "Couple" },
        detail: { es: "Dos personas aseguradas. 100% de los beneficios.", en: "Two insured people. 100% of benefits." },
        featured: true,
      },
      {
        id: "familiar",
        name: { es: "Familiar", en: "Family" },
        detail: { es: "Toda la familia asegurada. 100% de los beneficios.", en: "The whole family insured. 100% of benefits." },
      },
    ],
  },

  {
    id: "cancer",
    image: cancerImg,
    icon: "ribbon",
    name: { es: "Seguro de Cáncer", en: "Cancer Insurance" },
    tag: { es: "Protección específica", en: "Focused protection" },
    promise: {
      es: "Dinero en efectivo desde el primer diagnóstico, para que el tratamiento no dependa de tu cuenta de banco.",
      en: "Cash from the first diagnosis, so treatment does not depend on your bank account.",
    },
    forWho: {
      es: "Personas con historial familiar de cáncer o que quieren blindar sus ahorros ante un diagnóstico.",
      en: "People with a family history of cancer, or anyone who wants to shield their savings from a diagnosis.",
    },
    summary: {
      es: "Cubre gastos médicos, tratamientos y costos asociados que tu plan médico no paga: transportación, hospedaje cerca del hospital, terapias, y el ingreso que dejas de generar mientras te tratas.",
      en: "Covers medical expenses, treatments and associated costs your health plan does not pay: transportation, lodging near the hospital, therapies, and the income you stop earning during treatment.",
    },
    coverages: [
      {
        amount: "$57,000",
        title: { es: "Primer diagnóstico", en: "First diagnosis" },
        detail: {
          es: "Pago en efectivo de libre uso al confirmarse el diagnóstico.",
          en: "Cash benefit with no usage restrictions upon confirmed diagnosis.",
        },
      },
      {
        amount: "50+",
        title: { es: "Beneficios de tratamiento", en: "Treatment benefits" },
        detail: {
          es: "Quimioterapia, radioterapia, cirugías, prótesis, transportación y hospedaje.",
          en: "Chemotherapy, radiation, surgery, prosthetics, transportation and lodging.",
        },
      },
      {
        amount: "24/7",
        title: { es: "Acompañamiento de tu asesor", en: "Advisor support" },
        detail: {
          es: "Te ayudamos a radicar y dar seguimiento a cada reclamación.",
          en: "We help you file and follow up on every claim.",
        },
      },
    ],
    notCovered: {
      es: [
        "Cáncer diagnosticado antes de la fecha de efectividad de la póliza.",
        "Aplica período de espera inicial según la aseguradora.",
        "Cáncer de piel no melanoma puede tener beneficio reducido.",
      ],
      en: [
        "Cancer diagnosed before the policy effective date.",
        "An initial waiting period applies depending on the carrier.",
        "Non-melanoma skin cancer may carry a reduced benefit.",
      ],
    },
    ask: {
      es: [
        "¿Cubre recurrencia si ya tuve cáncer y estoy en remisión?",
        "¿El beneficio se paga una sola vez o por etapa de tratamiento?",
        "¿Qué documentos médicos exige la aseguradora?",
      ],
      en: [
        "Does it cover recurrence if I already had cancer and I am in remission?",
        "Is the benefit paid once or per treatment stage?",
        "What medical documents does the carrier require?",
      ],
    },
  },

  {
    id: "gastos-finales",
    image: gastosImg,
    icon: "heart",
    name: { es: "Gastos Finales", en: "Final Expenses" },
    tag: { es: "Tranquilidad familiar", en: "Family peace of mind" },
    promise: {
      es: "Que tu partida no se convierta en una deuda para quienes te sobreviven.",
      en: "So your passing never becomes a debt for those you leave behind.",
    },
    forWho: {
      es: "Adultos de 45 años en adelante y cualquier persona que quiera dejar resuelto el costo del funeral.",
      en: "Adults 45 and older, and anyone who wants funeral costs settled in advance.",
    },
    summary: {
      es: "Un funeral en Puerto Rico ronda entre $8,000 y $12,000. Esta póliza entrega el dinero a tu beneficiario en días, no en meses, para cubrir servicios funerarios, deudas pendientes y los primeros gastos de la familia.",
      en: "A funeral in Puerto Rico runs between $8,000 and $12,000. This policy delivers money to your beneficiary in days, not months, to cover funeral services, pending debts and the family's first expenses.",
    },
    coverages: [
      {
        amount: "$5k – $25k",
        title: { es: "Beneficio por fallecimiento", en: "Death benefit" },
        detail: {
          es: "Monto que eliges según el costo real del servicio que deseas.",
          en: "Amount you choose based on the real cost of the service you want.",
        },
      },
      {
        amount: { es: "Sin examen", en: "No exam" },
        title: { es: "Emisión simplificada", en: "Simplified issue" },
        detail: {
          es: "Varias opciones se emiten solo con preguntas de salud, sin examen médico.",
          en: "Several options are issued with health questions only, no medical exam.",
        },
      },
      {
        amount: { es: "Prima fija", en: "Level premium" },
        title: { es: "No sube con la edad", en: "Does not rise with age" },
        detail: {
          es: "La prima que firmas hoy es la que pagas siempre en los planes nivelados.",
          en: "The premium you sign today is what you pay for life on level plans.",
        },
      },
    ],
    notCovered: {
      es: [
        "Los planes de emisión simplificada suelen tener 2 años de período graduado por muerte natural.",
        "Declarar información de salud incompleta puede anular el beneficio.",
        "No es un plan de ahorro ni de inversión.",
      ],
      en: [
        "Simplified issue plans typically carry a 2-year graded period for natural death.",
        "Incomplete health disclosures can void the benefit.",
        "It is not a savings or investment plan.",
      ],
    },
    ask: {
      es: [
        "¿El plan es nivelado o graduado? ¿Qué pasa si fallezco en los primeros dos años?",
        "¿Cuánto tarda la aseguradora en pagar al beneficiario?",
        "¿Puedo nombrar más de un beneficiario y cambiarlo después?",
      ],
      en: [
        "Is the plan level or graded? What happens if I pass in the first two years?",
        "How long does the carrier take to pay the beneficiary?",
        "Can I name more than one beneficiary and change it later?",
      ],
    },
  },

  {
    id: "retiro",
    image: retiroImg,
    icon: "trending",
    name: { es: "Retiro y Ahorro", en: "Retirement & Savings" },
    tag: { es: "Construye tu futuro", en: "Build your future" },
    promise: {
      es: "Un ingreso que siga llegando cuando el cheque del trabajo deje de llegar.",
      en: "Income that keeps arriving when the paycheck stops.",
    },
    forWho: {
      es: "Profesionales y dueños de negocio que quieren complementar el Seguro Social y proteger su capital.",
      en: "Professionals and business owners who want to complement Social Security and protect their capital.",
    },
    summary: {
      es: "Opciones de acumulación y anualidades diseñadas para generar un flujo de ingreso estable durante la jubilación, con reglas claras sobre acceso al dinero, penalidades y tratamiento contributivo en Puerto Rico.",
      en: "Accumulation options and annuities designed to generate stable retirement income, with clear rules on access to your money, penalties and Puerto Rico tax treatment.",
    },
    coverages: [
      {
        amount: { es: "Ingreso vitalicio", en: "Lifetime income" },
        title: { es: "Opción de renta garantizada", en: "Guaranteed income option" },
        detail: {
          es: "Convierte lo acumulado en pagos periódicos que no se agotan.",
          en: "Turns what you accumulated into periodic payments that do not run out.",
        },
      },
      {
        amount: { es: "Piso protegido", en: "Protected floor" },
        title: { es: "Protección del principal", en: "Principal protection" },
        detail: {
          es: "Alternativas donde una caída del mercado no reduce tu capital acumulado.",
          en: "Alternatives where a market drop does not reduce your accumulated capital.",
        },
      },
      {
        amount: { es: "Flexible", en: "Flexible" },
        title: { es: "Aportaciones a tu ritmo", en: "Contribute at your pace" },
        detail: {
          es: "Ajusta la aportación según tu etapa de vida y tus ingresos.",
          en: "Adjust contributions to your life stage and income.",
        },
      },
    ],
    notCovered: {
      es: [
        "Retirar antes del tiempo pactado puede generar cargos por rescate.",
        "El tratamiento contributivo depende de tu situación particular; consulta a tu CPA.",
        "Los productos indexados tienen topes de participación en el rendimiento.",
      ],
      en: [
        "Withdrawing before the agreed term may trigger surrender charges.",
        "Tax treatment depends on your particular situation; consult your CPA.",
        "Indexed products carry participation caps on returns.",
      ],
    },
    ask: {
      es: [
        "¿Cuál es el cargo por rescate y por cuántos años aplica?",
        "¿Qué comisiones y gastos anuales tiene el producto?",
        "¿Qué pasa con el dinero si fallezco antes de comenzar a recibir la renta?",
      ],
      en: [
        "What is the surrender charge and how many years does it apply?",
        "What annual fees and expenses does the product carry?",
        "What happens to the money if I pass before income starts?",
      ],
    },
  },
];

export const getProduct = (id) => PRODUCTS.find((p) => p.id === id);
